import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { CheckIcon, EditIcon, LeafIcon, SparkleIcon } from "@/app/components/system/Icons";
import { Button } from "@/app/components/system/Button";
import { PlantMark } from "@/app/components/PlantMark";
import { PlantPhotoPicker } from "@/app/components/PlantPhotoPicker";
import { ScreenHead } from "@/app/components/ScreenHead";
import { SpeciesCard } from "@/app/components/SpeciesCard";
import { Field } from "@/app/components/Field";
import { PotPicker, type PotPickerForm } from "@/app/components/PotPicker";
import { usePots } from "../lib/data-context";
import { relDayToISO } from "@/app/lib/dates";
import { isSunRangeInvalid } from "@/app/lib/sunHours";
import type { Species } from "@/types";

interface ScanResultState {
  result: Omit<Species, "id"> & { id?: string };
  defaultPotId?: string;
  fromKnown: boolean;
  fromManual: boolean;
  // Where the user arrived from, so the heading reads correctly ("Plant
  // identified" for a photo, "Label decoded" for a care tag).
  source?: "photo" | "label";
  photoUpload?: { id: string; url: string } | null;
}

export function ScanResultScreen() {
  const location = useLocation();
  const state = location.state as ScanResultState | null;
  if (!state?.result) return <Navigate to="/add" replace />;
  return <ScanResultInner {...state} />;
}

function ScanResultInner({ result, defaultPotId, fromKnown, fromManual, source, photoUpload: initialPhotoUpload }: ScanResultState) {
  const navigate = useNavigate();
  const { pots, addPlant } = usePots();
  const todayISO = relDayToISO(0) ?? "";
  const [nickname, setNickname] = useState("");
  const [plantedAt, setPlantedAt] = useState(todayISO);
  const [potForm, setPotForm] = useState<PotPickerForm>({
    potChoice: defaultPotId ? "existing" : "new",
    potId: defaultPotId || pots[0]?.id || "",
    newPotName: "",
    newPotShape: "terracotta",
    newPotSize: "medium",
    newPotSunStart: "",
    newPotSunEnd: "",
  });
  const [photoUpload, setPhotoUpload] = useState<{ id: string; url: string } | null>(initialPhotoUpload ?? null);
  const r = result;
  // When the user came from a specific pot, that pot is fixed — show it read-only.
  const lockedPot = defaultPotId ? pots.find((p) => p.id === defaultPotId) ?? null : null;

  const inFuture = !!plantedAt && plantedAt > todayISO;
  const sunInvalid = potForm.potChoice === "new"
    && isSunRangeInvalid(potForm.newPotSunStart || null, potForm.newPotSunEnd || null);

  const kicker = fromManual ? (
    <p className="kicker"><EditIcon size={12} /> editable defaults</p>
  ) : fromKnown ? (
    <p className="kicker"><LeafIcon size={12} /> from your library</p>
  ) : (
    <p className="kicker"><SparkleIcon size={12} /> care notes ready</p>
  );

  const confirm = async () => {
    if (!plantedAt || inFuture || sunInvalid) return;
    const base = {
      nickname: nickname.trim() || r.common,
      species: r.id,
      plantedAt,
      photoImageId: photoUpload?.id ?? null,
    };
    if (potForm.potChoice === "existing" && potForm.potId) {
      await addPlant({ ...base, potId: potForm.potId });
    } else {
      await addPlant({
        ...base,
        newPot: {
          name: potForm.newPotName.trim() || `${base.nickname}'s pot`,
          shape: potForm.newPotShape,
          size: potForm.newPotSize,
          sunStart: potForm.newPotSunStart || null,
          sunEnd: potForm.newPotSunEnd || null,
        },
      });
    }
    navigate("/");
  };

  return (
    <div className="screen pad">
      <ScreenHead
        title={fromManual ? "Add manually" : fromKnown ? "Add another" : source === "photo" ? "Plant identified" : "Label decoded"}
        subtitle={fromManual ? "Defaults below — tweak anything before saving." : "Confirm and add to your garden."}
        onBack={() => navigate(defaultPotId ? `/add?potId=${defaultPotId}` : "/add")} />

      {r.id ? (
        <SpeciesCard species={r} kicker={kicker} />
      ) : (
        <div className="bg-paper border border-dashed border-line rounded-2xl p-4.5 mt-2 mb-5.5">
          {kicker}
          <div className="flex items-start gap-3">
            <span className="shrink-0 w-9 h-9 rounded-xl bg-cream-2 text-ink-3 grid place-items-center">
              <LeafIcon size={18} />
            </span>
            <div className="min-w-0">
              <p className="font-display text-[15px] text-ink m-0 mb-1 tracking-tight">No care recommendations</p>
              <p className="text-[13px] text-ink-3 m-0 leading-[1.45]">
                We don't know this plant's species, so we can't suggest light, water, soil, or fertilizer. Scan a label to fill these in.
              </p>
            </div>
          </div>
        </div>
      )}

      <h3 className="form-h">Make it yours</h3>
      <div className="grid place-items-center pt-2 pb-5 mb-5.5 bg-cream-2 rounded-[18px] border border-line">
        <PlantPhotoPicker
          photoImageUrl={photoUpload?.url ?? null}
          size={140}
          fallback={
            <div
              className="grid place-items-center w-full h-full"
              style={{ background: r.color }}
            >
              <PlantMark plant={{ id: "preview", species: { initial: r.initial, color: r.color, labelImageUrl: null } }} size={100} />
            </div>
          }
          onChange={setPhotoUpload}
        />
      </div>
      <Field label="Nickname"><input placeholder={r.common} value={nickname} onChange={(e) => setNickname(e.target.value)} /></Field>
      <Field label="Planted on">
        <input type="date" required value={plantedAt} max={todayISO} onChange={(e) => setPlantedAt(e.target.value)} />
        {inFuture && <p className="font-sans text-xs text-terra m-0 mt-1">Planted on can't be in the future.</p>}
      </Field>

      <PotPicker pots={pots} form={potForm} setForm={setPotForm} lockedPot={lockedPot} />

      <div className="flex gap-2.5 mt-6">
        {!fromKnown && !fromManual && (
          <Button variant="secondary" className="shrink-0" onClick={() => navigate(defaultPotId ? `/add?potId=${defaultPotId}` : "/add")}>Re-scan</Button>
        )}
        <Button className="flex-1" disabled={!plantedAt || inFuture || sunInvalid} onClick={confirm}>
          <CheckIcon size={16} stroke={2.2} /> Add to garden
        </Button>
      </div>
    </div>
  );
}
